import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFixedTooltip } from '../../hooks/useFixedTooltip';
import { useTruncatedTooltip } from '@/core/hooks/useTruncatedTooltip';
import type { MenuLocale } from '../../constants/menuLabels';
import {
  BdsAvatar,
  BdsTypo,
  BdsIcon,
} from '@/core/constants/webComponents';

import './SearchSelector.scss';

type SearchOption = {
  label: string;
  universeId: string;
};

interface SearchSelectorProps {
  profileName?: string;
  avatarUrl?: string;
  searchOptions?: Array<string | { label?: string; name?: string; universeId?: string; id?: string; value?: string }>;
  selectedSearch?: string;
  universeId?: string;
  searchChangeEventName?: string;
  locale?: MenuLocale;
}

export const SearchSelector: React.FC<SearchSelectorProps> = ({
  profileName,
  avatarUrl,
  searchOptions,
  selectedSearch,
  universeId,
  searchChangeEventName = 'mfe-menu-search-change',
}) => {
  const { t } = useTranslation();
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const {
    tooltip: optionTooltip,
    showTooltip: showOptionTooltip,
    hideTooltip: hideOptionTooltip,
  } = useTruncatedTooltip({ targetSelector: '.search-selector__text-truncate' });

  const labels = (t('menu', { returnObjects: true }) || {}) as Record<string, string>;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        activatorRef.current?.focus(); 
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const resolvedOptions = useMemo<SearchOption[]>(() => {
    if (searchOptions && searchOptions.length > 0) {
      return searchOptions
        .map(option => {
          if (typeof option === 'string') {
            return {
              label: option,
              universeId: option,
            };
          }

          const label = option.label ?? option.name ?? '';
          const optionUniverseId = option.universeId ?? option.id ?? option.value ?? '';

          if (!label || !optionUniverseId) {
            return null;
          }

          return {
            label,
            universeId: optionUniverseId,
          };
        })
        .filter((option): option is SearchOption => Boolean(option));
    }

    return [
      { label: 'Nome da Pesquisa 1', universeId: 'universe-1' },
      { label: 'Nome da Pesquisa 2', universeId: 'universe-2' },
    ];
  }, [searchOptions]);

  const initialSelectedUniverseId = useMemo(() => {
    if (universeId && resolvedOptions.some(option => option.universeId === universeId)) {
      return universeId;
    }

    if (selectedSearch) {
      const selectedByName = resolvedOptions.find(option => option.label === selectedSearch);
      if (selectedByName) {
        return selectedByName.universeId;
      }
    }

    return resolvedOptions[0]?.universeId ?? '';
  }, [resolvedOptions, selectedSearch, universeId]);

  const [currentUniverseId, setCurrentUniverseId] = useState(initialSelectedUniverseId);
  const handleTooltipPosition = useFixedTooltip('.search-selector__custom-tooltip', 'right');

  const profileTooltipRef = useRef<HTMLButtonElement>(null);
  const activatorRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleTooltipPositionListener = handleTooltipPosition as unknown as EventListener;

    const attach = (ref: React.RefObject<HTMLElement | null>, type: string, handler: EventListener) => {
      const el = ref.current;
      el?.addEventListener(type, handler);
      return () => el?.removeEventListener(type, handler);
    };

    const cleanup = [
      attach(profileTooltipRef, 'mouseenter', handleTooltipPositionListener),
      attach(profileTooltipRef, 'focus', handleTooltipPositionListener),
      attach(activatorRef, 'click', () => setIsOpen((prev) => !prev)),
      attach(activatorRef, 'mouseenter', handleTooltipPositionListener),
      attach(activatorRef, 'focus', handleTooltipPositionListener),
    ];

    return () => cleanup.forEach((fn) => fn());
  }, [handleTooltipPosition]);

  useEffect(() => {
    setCurrentUniverseId(initialSelectedUniverseId);
  }, [initialSelectedUniverseId]);

  const emitSearchChange = (selectedUniverseId: string) => {
    const eventTarget = selectorRef.current ?? document;
    const selectedOption = resolvedOptions.find(option => option.universeId === selectedUniverseId);

    eventTarget.dispatchEvent(
      new CustomEvent(searchChangeEventName, {
        detail: {
          searchName: selectedOption?.label,
          universeId: selectedUniverseId,
        },
        bubbles: true,
        composed: true,
      }),
    );
  };

  const handleSearchChange = (selectedUniverseId: string) => {
    setCurrentUniverseId(selectedUniverseId);
    emitSearchChange(selectedUniverseId);
    hideOptionTooltip();
    setIsOpen(false);
  };

  useEffect(() => {
    if (isOpen) {
      requestAnimationFrame(() => {
        const dropdown = dropdownRef.current?.querySelector('.search-selector__dropdown-content') as HTMLElement;
        if (dropdown && dropdownRef.current) {
          const rect = dropdownRef.current.getBoundingClientRect();
          dropdown.style.position = 'fixed';
          dropdown.style.top = `${rect.bottom + 8}px`;
          dropdown.style.left = `${rect.left}px`;
        }
      });
    }
  }, [isOpen]);

  const currentSearchLabel = useMemo(() => {
    return resolvedOptions.find(opt => opt.universeId === currentUniverseId)?.label ?? '';
  }, [resolvedOptions, currentUniverseId]);

  return (
    <div className="search-selector" data-test="menu-search-selector" ref={selectorRef}>
      {profileName ? (
        <BdsAvatar size="standard" name={profileName} thumbnail={avatarUrl} tabIndex={-1} aria-hidden="true"></BdsAvatar>
      ) : (
        <span className="search-selector__avatar-placeholder" aria-hidden="true" />
      )}

      <div className="search-selector__content">
        <button 
          ref={profileTooltipRef}
          type="button" 
          className="search-selector__tooltip-wrapper" 
        >
          <BdsTypo variant="fs-12" margin={false}>
            <span className="search-selector__text-truncate">
              {profileName}
            </span>
          </BdsTypo>
          <span className="search-selector__custom-tooltip" role="tooltip">
            {profileName}
          </span>
        </button>
        <div className="search-selector__dropdown-container" ref={dropdownRef}>
          <button
            ref={activatorRef}
            type="button"
            className="search-selector__activator"
            aria-haspopup="true"
            aria-expanded={isOpen}
          >
            <div className="search-selector__tooltip-wrapper search-selector__tooltip-wrapper--full">
              <BdsTypo variant="fs-16" bold="bold" margin={false}>
                <span className="search-selector__text-truncate search-selector__text-truncate--bold">
                  {currentSearchLabel}
                </span>
              </BdsTypo>
              <span className="search-selector__custom-tooltip" role="tooltip">{currentSearchLabel}</span>
            </div>
            <BdsIcon name="arrow-down" color="surface-0"></BdsIcon>
          </button>

          {isOpen && (
            <div className="search-selector__dropdown-content">
              <div className="search-selector__dropdown-header">
                <BdsTypo variant="fs-16" margin={false} bold="bold">
                  {labels.changeSearch}
                </BdsTypo>
              </div>
              <div className="search-selector__dropdown-divider" />
              <ul className="search-selector__options">
                {resolvedOptions.map((option) => (
                  <li key={option.universeId}>
                    <button
                      type="button"
                      className={`search-selector__option ${
                        currentUniverseId === option.universeId ? 'search-selector__option--selected' : ''
                      }`}
                      title={option.label}
                      aria-label={option.label}
                      onMouseEnter={(event) => showOptionTooltip(event, option.label)}
                      onFocus={(event) => showOptionTooltip(event, option.label)}
                      onMouseLeave={hideOptionTooltip}
                      onBlur={hideOptionTooltip}
                      onClick={() => handleSearchChange(option.universeId)}
                    >
                      <BdsTypo variant="fs-14" margin={false}>
                        <span className="search-selector__text-truncate">
                          {option.label}
                        </span>
                      </BdsTypo>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {optionTooltip && (
            <span
              className="search-selector__option-tooltip"
              role="tooltip"
              style={{
                top: `${optionTooltip.top}px`,
                left: `${optionTooltip.left}px`,
              }}
            >
              {optionTooltip.text}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
